import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CFormCheck, CButton, CBadge, CInputGroup, CInputGroupText, CFormInput, CCard, CCardBody } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSearch, cilX, cilInfo } from '@coreui/icons';

const ReportScope = ({ config, updateConfig, devices, workZones }) => {
  const [reportDeviceSearch, setReportDeviceSearch] = useState('');

  const toggleReportDevice = (id) => {
    const isSelected = config.report_devices.includes(id);
    updateConfig({
      report_devices: isSelected
        ? config.report_devices.filter((devId) => devId !== id)
        : [...config.report_devices, id],
    });
  };

  const toggleReportZone = (id) => {
    const isSelected = config.report_zones.includes(id);
    updateConfig({
      report_zones: isSelected
        ? config.report_zones.filter((zId) => zId !== id)
        : [...config.report_zones, id],
    });
  };

  const filteredReportDevices = useMemo(() => {
    const filtered = devices.filter((d) =>
      d.name.toLowerCase().includes(reportDeviceSearch.toLowerCase())
    );
    filtered.sort((a, b) => {
      const aSelected = config.report_devices.includes(a.id);
      const bSelected = config.report_devices.includes(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.name.localeCompare(b.name);
    });
    return filtered;
  }, [devices, reportDeviceSearch, config.report_devices]);

  const selectedReportDevices = useMemo(() => {
    return devices.filter((d) => config.report_devices.includes(d.id));
  }, [devices, config.report_devices]);

  return (
    <CCard className="shadow-sm border-0 bg-white">
      <CCardBody className="p-3">
        <div className="alert alert-info py-2 small d-flex align-items-center mb-3">
           <CIcon icon={cilInfo} className="me-2" size="xl" />
           <span>Выбранная здесь техника будет использоваться для <strong>генерации Excel-отчетов</strong> и <strong>отслеживания норм (План/Факт)</strong>.</span>
        </div>

        <div className="mb-3 d-flex flex-column flex-md-row gap-3 bg-light p-3 rounded border">
          <CFormCheck
            type="radio"
            name="rep_type"
            id="rep_all"
            label={<span className="fw-bold">Весь автопарк (Сводный отчет)</span>}
            checked={config.report_all_devices && !config.report_by_zones}
            onChange={() => updateConfig({ report_all_devices: true, report_by_zones: false })}
          />
          <CFormCheck
            type="radio"
            name="rep_type"
            id="rep_zones"
            label={<span className="fw-bold">По Участкам (1 участок = 1 отчет)</span>}
            checked={config.report_by_zones}
            onChange={() => updateConfig({ report_all_devices: false, report_by_zones: true })}
          />
          <CFormCheck
            type="radio"
            name="rep_type"
            id="rep_custom"
            label={<span className="fw-bold">Выбрать конкретные экскаваторы</span>}
            checked={!config.report_all_devices && !config.report_by_zones}
            onChange={() => updateConfig({ report_all_devices: false, report_by_zones: false })}
          />
        </div>

        {/* ВЫБОР УЧАСТКОВ */}
        {config.report_by_zones && (
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold text-dark">Отметьте участки:</span>
              <div>
                <CButton size="sm" color="info" variant="ghost" onClick={() => updateConfig({ report_zones: workZones.map((z) => z.id) })}>Выбрать все</CButton>
                <CButton size="sm" color="danger" variant="ghost" onClick={() => updateConfig({ report_zones: [] })}>Сбросить</CButton>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {workZones.map((z) => {
                const isSelected = config.report_zones.includes(z.id);
                return (
                  <div
                    key={z.id}
                    className={`d-flex align-items-center p-2 border rounded ${isSelected ? 'border-primary bg-primary text-white' : 'bg-light text-dark'}`}
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onClick={() => toggleReportZone(z.id)}
                  >
                    <CFormCheck checked={isSelected} readOnly style={{ pointerEvents: 'none', margin: 0 }} />
                    <label className="ms-2 mb-0 fw-semibold" style={{ pointerEvents: 'none' }}>{z.name}</label>
                  </div>
                );
              })}
              {workZones.length === 0 && <span className="text-muted small">Участки не созданы. Добавьте их в разделе "Автопарк".</span>}
            </div>
          </div>
        )}

        {/* ВЫБОР ЭКСКАВАТОРОВ */}
        {!config.report_all_devices && !config.report_by_zones && (
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold text-dark">Отметьте технику:</span>
              <div>
                <CButton size="sm" color="info" variant="ghost" onClick={() => updateConfig({ report_devices: devices.map((d) => d.id) })}>Выбрать все</CButton>
                <CButton size="sm" color="danger" variant="ghost" onClick={() => updateConfig({ report_devices: [] })}>Сбросить</CButton>
              </div>
            </div>

            {selectedReportDevices.length > 0 && (
              <div className="mb-2 p-2 border rounded bg-light d-flex flex-wrap gap-2">
                {selectedReportDevices.map((d) => (
                  <CBadge
                    key={`b-${d.id}`}
                    color="primary"
                    className="d-flex align-items-center p-2 text-white shadow-sm"
                    style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                    onClick={() => toggleReportDevice(d.id)}
                  >
                    <span className="me-2">{d.name}</span>
                    <CIcon icon={cilX} size="sm" />
                  </CBadge>
                ))}
              </div>
            )}

            <CInputGroup size="sm" className="mb-2 w-50">
              <CInputGroupText className="bg-light border-end-0"><CIcon icon={cilSearch} size="sm" /></CInputGroupText>
              <CFormInput className="border-start-0 ps-0 bg-light" placeholder="Поиск техники..." value={reportDeviceSearch} onChange={(e) => setReportDeviceSearch(e.target.value)} />
            </CInputGroup>

            <div className="d-flex flex-wrap gap-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {filteredReportDevices.map((d) => {
                const isSelected = config.report_devices.includes(d.id);
                return (
                  <div
                    key={d.id}
                    className={`d-flex align-items-center p-2 border rounded ${isSelected ? 'border-primary bg-primary text-white' : 'bg-light text-dark'}`}
                    style={{ cursor: 'pointer', transition: 'all 0.2s', fontSize: '0.85rem' }}
                    onClick={() => toggleReportDevice(d.id)}
                  >
                    <CFormCheck checked={isSelected} readOnly style={{ pointerEvents: 'none', margin: 0 }} />
                    <label className="ms-2 mb-0 fw-semibold" style={{ pointerEvents: 'none' }}>{d.name}</label>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CCardBody>
    </CCard>
  );
};

ReportScope.propTypes = {
  config: PropTypes.object.isRequired,
  updateConfig: PropTypes.func.isRequired,
  devices: PropTypes.array.isRequired,
  workZones: PropTypes.array.isRequired,
};

export default ReportScope;